import React from 'react';
import PropTypes from 'prop-types';
import I18n from 'modules/i18n';
import { useOpenContactAdminsModal } from './ContactAdminsModal';

// AIDEV-NOTE: Tooltip message bodies for the disabled Add/Remove Programs items in
// ManagePrograms. The "admins" trigger calls the ContactAdminsModalProvider hosted at the
// ManagePrograms level so the modal survives when the tooltip closes.

const adminButtonClass = 'tw-bg-transparent tw-border-none tw-p-0 tw-text-tci-purple ' +
  'tw-underline tw-cursor-pointer tw-font-inherit';

const AdminMessage = ({ adminDashPath }) => {
  const isSpanish = I18n.locale === 'es';

  return (
    <p>
      {isSpanish ?
        'Su distrito está administrando el acceso al programa. Si está registrado automáticamente,' :
        'Your district is managing program access. If you are autorostered'}
      {' '}
      <a href="https://www.teachtci.com/exclude-teacher" rel="noopener noreferrer" target="_blank">
        {isSpanish ? 'exclúyase de la asignación automática' : 'exclude yourself from autorostering'}
      </a>
      {isSpanish ? ' y ' : ' and '}
      <a href="https://www.teachtci.com/add-remove-program-from-teacher" rel="noopener noreferrer" target="_blank">
        {isSpanish ? 'administre sus programas.' : 'manage your programs.'}
      </a>
      {' '}
      {isSpanish ?
        'De lo contrario, administre su configuración de bloqueo en el' :
        'Otherwise, manage your lock settings on the'}
      {' '}
      <a href={adminDashPath}>
        {isSpanish ? 'panel de administración.' : 'admin dashboard.'}
      </a>
    </p>
  );
};

AdminMessage.propTypes = {
  adminDashPath: PropTypes.string.isRequired,
};

export const TeacherMessage = ({ getAdminInfoPath }) => {
  const openModal = useOpenContactAdminsModal();

  return (
    <p>
      Your district manages your programs. Please contact your
      {' '}
      <button
        aria-label={I18n.t('contact_administrators')}
        className={adminButtonClass}
        type="button"
        onClick={() => openModal(getAdminInfoPath)}
      >
        admins
      </button>
      {' '}
      to add a program.
    </p>
  );
};

TeacherMessage.propTypes = {
  getAdminInfoPath: PropTypes.string.isRequired,
};

export const TooltipMessage = ({ adminDashPath, getAdminInfoPath, userType }) => {
  if (userType === 'Admin') {
    return <AdminMessage adminDashPath={adminDashPath} />;
  }
  return <TeacherMessage getAdminInfoPath={getAdminInfoPath} />;
};

TooltipMessage.propTypes = {
  adminDashPath: PropTypes.string.isRequired,
  getAdminInfoPath: PropTypes.string.isRequired,
  userType: PropTypes.string.isRequired,
};

const AllSeatsInUseMessageAdmin = ({ adminProgramsPath }) => (
  <p>
    All teacher licenses for the programs are currently in use.
    Go to
    {' '}
    <a href={adminProgramsPath}>Programs</a>
    {' '}
    to view your district&apos;s current program usage and
    {' '}
    <a href="https://www.teachtci.com/add-remove-program-from-teacher" rel="noopener noreferrer" target="_blank">
      manage
    </a>
    {' '}
    your staff&apos;s program assignments.
  </p>
);

AllSeatsInUseMessageAdmin.propTypes = {
  adminProgramsPath: PropTypes.string.isRequired,
};

const AllSeatsInUseMessageTeacher = ({ getAdminInfoPath }) => {
  const openModal = useOpenContactAdminsModal();

  return (
    <p>
      All teacher licenses for your district’s programs are currently in use.
      {' '}
      Contact your
      {' '}
      <button
        aria-label={I18n.t('contact_administrators')}
        className={adminButtonClass}
        type="button"
        onClick={() => openModal(getAdminInfoPath)}
      >
        admins
      </button>
      {' '}
      to get access to a program.
    </p>
  );
};

AllSeatsInUseMessageTeacher.propTypes = {
  getAdminInfoPath: PropTypes.string.isRequired,
};

export const DisabledMessage = ({
  adminDashPath, adminProgramsPath, getAdminInfoPath, manuallyManaged, userType,
}) => {
  if (manuallyManaged) {
    return (
      <TooltipMessage
        adminDashPath={adminDashPath}
        getAdminInfoPath={getAdminInfoPath}
        userType={userType}
      />
    );
  }

  if (userType === 'Admin') {
    return <AllSeatsInUseMessageAdmin adminProgramsPath={adminProgramsPath} />;
  }

  return <AllSeatsInUseMessageTeacher getAdminInfoPath={getAdminInfoPath} />;
};

DisabledMessage.propTypes = {
  adminDashPath: PropTypes.string.isRequired,
  adminProgramsPath: PropTypes.string.isRequired,
  getAdminInfoPath: PropTypes.string.isRequired,
  manuallyManaged: PropTypes.bool.isRequired,
  userType: PropTypes.string.isRequired,
};
